import React from 'react';
import { LocuslexWidget } from '@karibew/locilex-widget';
import '@karibew/locilex-widget/dist/style.css';
import { useAuth } from '../../context/AuthContext';
import { useAsync } from '../../hooks/useAsync';
import { clientsAPI, Client } from '../../services/api';

// The widget is consumed from the published @karibew/locilex-widget package and
// talks directly to the Locilex backend. Configure via env (CRA requires the
// REACT_APP_ prefix):
//   REACT_APP_LOCILEX_URL      — Locilex backend (default http://localhost:4000)
//   REACT_APP_LOCILEX_API_KEY  — Locilex API key (from 1Password; without it the
//                                backend returns 403 and templates won't load)
const LOCILEX_URL = process.env.REACT_APP_LOCILEX_URL || 'http://localhost:4000';
const LOCILEX_KEY = process.env.REACT_APP_LOCILEX_API_KEY || '';

// The widget populates its client dropdown from the `submissions` prop (it reads
// client_name/email/phone/address from each). We map GrantEx clients into that
// shape so the dropdown lists the organization's real clients — same approach as
// the Soumigo integration, which feeds its submissions through this prop.
interface WidgetSubmission {
  id: number;
  title: string;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  client_address?: string;
}

function clientsToSubmissions(clients: Client[] | null): WidgetSubmission[] {
  return (clients || []).map((c) => ({
    id: c.id,
    title: c.company || c.name,
    client_name: c.name,
    client_email: c.email,
    client_phone: c.phone,
    client_address: c.address,
  }));
}

const ClausesPage: React.FC = () => {
  const { user } = useAuth();

  // Any authenticated user may list their organization's clients (the index
  // action isn't manager-gated), so developers on /clauses get the dropdown too.
  const { data: clients } = useAsync<Client[]>(
    () => clientsAPI.getAll().then((r) => r.data.clients as Client[]),
    [],
  );

  const company = {
    name: user?.organization?.name,
    email: user?.email,
  };

  return (
    <div style={{ height: 'calc(100vh - 64px)' }}>
      <LocuslexWidget
        apiKey={LOCILEX_KEY}
        backendUrl={LOCILEX_URL}
        context="construction"
        // 'parties' is enabled so an employee contract can list named parties
        // manually instead of selecting a client.
        features={['clauses', 'ai', 'signature', 'parties', 'dates', 'preamble']}
        submissions={clientsToSubmissions(clients)}
        company={company}
        senderName={user?.full_name}
        senderEmail={user?.email}
      />
    </div>
  );
};

export default ClausesPage;
